import { useCallback, useEffect, useRef } from 'react';
import {
  DefaultMenuItem,
  IMenuActionParams,
  MenuItemDef,
} from 'ag-grid-community';
import { isTruthy } from '@ardoq/common-helpers';
import { Column, Row, UseControlledAGGridProps } from '../types';
import { isGroupingColumn } from '../utils';
import { isValidAutoSizeCap, transformContextMenuItems } from '../menuItems';

const buildAutoSizeCaps = <RowData, RowId extends string = string>(
  columns: Column<RowData, RowId>[]
): ReadonlyMap<string, number> => {
  const caps = new Map<string, number>();
  for (const column of columns) {
    if (isValidAutoSizeCap(column.maxAutoSizeWidth)) {
      const agColId = isGroupingColumn(column)
        ? 'ag-Grid-AutoColumn'
        : String(column.id);
      caps.set(agColId, column.maxAutoSizeWidth);
    }
  }
  return caps;
};

export const useContextMenuItems = <
  RowData,
  FullWidthRowData = never,
  RowId extends string = string,
>(
  props: UseControlledAGGridProps<RowData, FullWidthRowData, RowId>
): ((
  params: IMenuActionParams<Row<RowData, RowId>>
) => (DefaultMenuItem | MenuItemDef<Row<RowData, RowId>>)[]) => {
  const columnsRef = useRef(props.columns);
  useEffect(() => {
    columnsRef.current = props.columns;
  }, [props.columns]);

  // Create a stable callback that looks up columns from the ref.
  const getContextMenuItemsCallback = useCallback(
    (params: IMenuActionParams<Row<RowData, RowId>>) => {
      const rowData = params.node?.data?.data;
      if (!rowData) {
        return [];
      }

      const autoSizeCaps = buildAutoSizeCaps(columnsRef.current);
      const localeText = params.api.getGridOption('localeText');
      const colId = params.column?.getColId();

      // Try to find column-level context menu first
      if (colId) {
        const column = columnsRef.current.find(
          currentColumn =>
            String(currentColumn.id) === colId ||
            (colId === 'ag-Grid-AutoColumn' && isGroupingColumn(currentColumn))
        );

        if (
          column &&
          'getContextMenuItems' in column &&
          column.getContextMenuItems
        ) {
          // Column has context menu - use it
          const selectedRows = params.api
            .getSelectedRows()
            .map(row => row?.data)
            .filter(isTruthy);
          const items = column.getContextMenuItems(rowData, selectedRows);
          return transformContextMenuItems(
            items,
            rowData,
            autoSizeCaps,
            localeText
          );
        }
      }

      // Fall back to row-level context menu
      const row = params.node?.data;
      if (row?.getContextMenuItems) {
        const selectedRows = params.api
          .getSelectedRows()
          .map(row => row?.data)
          .filter(isTruthy);
        const items = row.getContextMenuItems(rowData, selectedRows);
        return transformContextMenuItems(
          items,
          rowData,
          autoSizeCaps,
          localeText
        );
      }

      return [];
    },
    []
  );

  return getContextMenuItemsCallback;
};
